import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, FormSpy, useFormState } from "react-final-form";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import { Grid } from "@mui/material";
import Button from "@mui/material/Button";
import { LinearProgress } from "@mui/material";
import { PAGES } from "../App.jsx";
import { ConfirmDangerModalComponent } from "@soqs/common";

import {
  TaxNumberComponent,
  TaxTypeSelectComponent,
  NameField,
  SurnameField,
  IDNumberComponent,
  IDType,
  EmailFieldComponent,
  PhoneFieldComponent,
  OneColumn,
  TaxType,
  IDTypeSelectComponent,
  PassportComponent,
  PRNumberField,
  MenuItemsForDropDown,
  Capacity,
} from "@soqs/common";

import {
  updateFormState,
  isPrepop,
  isLoading,
  formState,
  dropdownoptions,
  dropdownsloading,
  submitFormAsync,
  cancelVerification,
  currentPage,
  setPage,
  updateCapacityAsync,
  selectedcapacity,
} from "../features/AppSlice";
import UserIdentificationComponent from "./UserIdentificationComponent.jsx";
import { config } from "../config";

export default function EnterDetails() {
  const dispatch = useDispatch();
  const loading = useSelector(isLoading);
  const form = useSelector(formState);
  const page = useSelector(currentPage);
  const prepop = useSelector(isPrepop);
  const selectedCapacity = useSelector(selectedcapacity);

  // Clean Internal Form State
  // const initialState = {
  //   IDType: IDType.RSA_Id,
  //   RepIdType: IDType.RSA_Id,
  //   TaxType: TaxType.PIT,
  //   Capacity: ""
  // };

  const dateToday = new Date();
  const taxYear =
    dateToday.getMonth() < 6 //getMonth returns 0 - 11 (Jan to Dec)
      ? (dateToday.getFullYear() - 1).toString()
      : dateToday.getFullYear().toString();

  const initialValues = {
    IDType: IDType.RSA_Id.toString(),
    RepIdType: IDType.RSA_Id.toString(),
    TaxType: TaxType.PIT.toString(),
    Capacity: selectedCapacity ?? "1",
    TaxYear: taxYear,

    //testing
    // Name: "Tester",
    // Surname: "Surname",
    // mobile_taxpayer: "0823384475",
    // TaxNumber: "3565009846",
    // IDNumber: "3407295023083",
  };

  const prefilledState = useMemo(
    () => form?.values ?? initialValues,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form?.values]
  );

  const ActivePage = () => {
    switch (page) {
      case PAGES.UserIdentification:
        return <UserIdentificationComponent />;
      case PAGES.EnterDetails:
        return <EnterDetailsPage />;
      default:
        return null;
    }
  };

  // Internal State
  const [confirmClearModal, setConfirmClearModal] = useState(false);

  const handleSubmit = async () => {
    dispatch(submitFormAsync());
  };

  const handleNext = async () => {
    if (prepop) {
      dispatch(updateCapacityAsync());
    }
    dispatch(setPage(1));
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Form onSubmit={handleSubmit} initialValues={prefilledState}>
      {({ handleSubmit, _submitting, _pristine, _form, values, _errors }) => (
        <>
          <Grid size={{ xs: 12 }}>
            <FormSpy
              onChange={(state) => {
                dispatch(updateFormState(state));
              }}
            />
            {ActivePage(page)}
            <Grid container justifyContent="space-between">
              <Grid>
                &nbsp;&nbsp;&nbsp;
                {page == 1 ? (
                  <Button
                    type="button"
                    aria-label="Restart"
                    variant="outlined"
                    onClick={() => {
                      setConfirmClearModal(true);
                    }}
                  >
                    Restart
                  </Button>
                ) : null}
              </Grid>
              <Grid>
                <Button
                  align="right"
                  loading={loading}
                  type="submit"
                  variant="contained"
                  onClick={page == 1 ? handleSubmit : handleNext}
                  aria-label="Verify Me"
                >
                  {page == 1 && !prepop ? "Verify Me" : "Next"}
                </Button>
                &nbsp;&nbsp;&nbsp;
              </Grid>
            </Grid>
          </Grid>
          <br />
          <ConfirmDangerModalComponent
            open={confirmClearModal}
            onClose={() => {
              setConfirmClearModal(false);
            }}
            cbConfirm={() => {
              dispatch(cancelVerification());
              setConfirmClearModal(false);
            }}
            title="Restart Capture"
            subheader="Are you sure you want to clear the form and start again?"
            confirmText="Restart"
          />
          {"development" === config.ENVIRONMENT ? (
            <Grid sx={{ display: { xs: "none", md: "block" } }}>
              <pre>{JSON.stringify(values, null, 2)}</pre>
            </Grid>
          ) : null}
        </>
      )}
    </Form>
  );
}

const EnterDetailsPage = () => {
  const prepop = useSelector(isPrepop);
  const dropdownopts = useSelector(dropdownoptions);
  const dropdownoptsloading = useSelector(dropdownsloading);
  const formState = useFormState();
  const selectedCapacity = formState.values.Capacity;

  return (
    <Grid size={{ xs: 12 }}>
      <Card>
        <CardContent>
          <CardHeader title="Taxpayer Details" />
          <Grid container columnSpacing={1.5} rowSpacing={0.5}>
            <OneColumn>
              <NameField
                name="Name"
                disabled={
                  prepop &&
                  selectedCapacity?.toString() === Capacity.Taxpayer.toString()
                }
                InputProps={{
                  required: false,
                }}
                aria-label="Taxpayer Name"
              />
            </OneColumn>
            <OneColumn>
              <SurnameField
                name="Surname"
                disabled={
                  prepop &&
                  selectedCapacity?.toString() ===
                    Capacity.Taxpayer.toString() &&
                  formState.values.SurnameField !== ""
                }
                aria-label="Taxpayer Surname"
              />
            </OneColumn>
            <OneColumn>
              <IDTypeSelectComponent
                name="IDType"
                idoptions={MenuItemsForDropDown("idtype", dropdownopts, true)}
                aria-label="Taxpayer ID Type"
                optionsloading={dropdownoptsloading}
                disabled={
                  prepop &&
                  selectedCapacity?.toString() === Capacity.Taxpayer.toString()
                }
                value={IDType.RSA_Id.toString()}
              />
            </OneColumn>
            <OneColumn>
              {String(formState.values?.IDType) === String(IDType.RSA_Id) ? (
                <IDNumberComponent
                  name="IDNumber"
                  disabled={
                    prepop &&
                    selectedCapacity?.toString() ===
                      Capacity.Taxpayer.toString()
                  }
                  aria-label="Taxpayer Tax Number"
                />
              ) : (
                <PassportComponent
                  name="Passport"
                  aria-label="Taxpayer Passport Number"
                />
              )}
            </OneColumn>
            <OneColumn>
              <TaxTypeSelectComponent
                name="TaxType"
                optionsloading={dropdownoptsloading}
                aria-label="Taxpayer Tax Type"
                taxoptions={MenuItemsForDropDown("taxtype", dropdownopts, true)}
                disabled
              />
            </OneColumn>
            <OneColumn>
              <TaxNumberComponent
                name="TaxNumber"
                disabled={
                  prepop &&
                  selectedCapacity?.toString() === Capacity.Taxpayer.toString()
                }
                aria-label="Taxpayer Tax Number"
              />
            </OneColumn>
            {selectedCapacity?.toString() ===
            Capacity.TaxPractitioner.toString() ? (
              <>
                <OneColumn>
                  <EmailFieldComponent
                    name="Email"
                    aria-label="Taxpayer Email"
                  />
                </OneColumn>
                <OneColumn>
                  <PhoneFieldComponent
                    variant="Mobile"
                    aria-label="Taxpayer Mobile"
                    required
                  />
                </OneColumn>
              </>
            ) : null}
          </Grid>
        </CardContent>
      </Card>
      <br />
      {selectedCapacity?.toString() !== Capacity.Taxpayer.toString() ? (
        <Card>
          <CardContent>
            {
              <CardHeader
                title={
                  selectedCapacity?.toString() ===
                  Capacity.TaxPractitioner.toString()
                    ? "Tax Practitioner Details"
                    : "Registered Representative Details"
                }
              />
            }
            <Grid container columnSpacing={1.5} rowSpacing={0.5}>
              <OneColumn>
                <NameField
                  name="RepName"
                  disabled={prepop}
                  aria-label="Representative Name"
                />
              </OneColumn>
              <OneColumn>
                <SurnameField
                  name="RepSurname"
                  disabled={prepop}
                  aria-label="Representative Surame"
                />
              </OneColumn>
              <OneColumn>
                <IDTypeSelectComponent
                  name="RepIdType"
                  aria-label="Representative ID Type"
                  idoptions={MenuItemsForDropDown("idtype", dropdownopts, true)}
                  optionsloading={dropdownoptsloading}
                  disabled={prepop}
                  value={IDType.RSA_Id.toString()}
                />
              </OneColumn>
              <OneColumn>
                {String(formState.values?.RepIdType) ===
                String(IDType.RSA_Id) ? (
                  <IDNumberComponent
                    name="RepId"
                    disabled={prepop}
                    aria-label="Representative ID Number"
                  />
                ) : (
                  <PassportComponent
                    name="RepPassport"
                    aria-label="Representative Passport Number"
                  />
                )}
              </OneColumn>

              {selectedCapacity?.toString() ===
              Capacity.TaxPractitioner.toString() ? (
                <>
                  <OneColumn>
                    <PRNumberField
                      aria-label="Tax Practitioner Number"
                      name="PRNumber"
                    />
                  </OneColumn>
                  <OneColumn />{" "}
                </>
              ) : null}
            </Grid>
          </CardContent>
        </Card>
      ) : null}
      <br />
    </Grid>
  );
};
